/* @flow */
// typecheck: ignore file
import { uniqBy } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import AnnouncementComposerRecipientsInput from 'groups/components/AnnouncementComposerRecipientsInput.jsx';
import Checkbox from 'components/Checkbox.jsx';
import GroupStore from 'groups/stores/GroupStore';
import PageControls from 'payments/components/PageControls.jsx';
import RoleBasedMessagingRecipientsInput from 'chat/components/RoleBasedMessagingRecipientsInput.jsx';
import Textarea from 'components/Textarea.jsx';
import { getTargetsFromGroupCodes } from 'lib/getTargetsFromGroupCodes';
import { MAX_PARTICIPANTS_REPLIES_ENABLE } from 'entities/Chat';
import { Target } from 'entities/Message';

import type { Recipient } from 'reducers/roleBasedMessagingSearch';

import './styles/ActivityAnnouncementChatMessageFormView.scss';

type Props = {
  message?: ?string,
  hasChildren?: ?boolean,
  onPreviewClick: (data: {message: string, hasChildren: boolean, disableReplies: boolean}) => void,
  disableReplies?: ?boolean,
  withRecipientInput?: boolean,
  showIndividualRecipients?: boolean,
  groupCodes?: ?string[],
  selection: Recipient[],
  targets: Target[],
  onNext: (data: {message: string, hasChildren: boolean, disableReplies: boolean}) => void,
  onBack: () => void,
  onInComposerChange?: (inComposer: boolean) => void,
  title: string,
};

type State = {
  message: string,
  groupCodes: string[],
  hasChildren: boolean,
  disableReplies: boolean,
  inComposer: boolean,
}

export class ActivityAnnouncementChatMessageFormView extends React.Component {
  props: Props;
  state: State;

  static defaultProps = {
    title: '',
    withRecipientInput: false,
    showIndividualRecipients: false,
  }

  constructor() {
    super(...arguments);
    const title = this.props.title || '';
    const message = this.props.message || `Hi everyone! I'm using Remind to coordinate the ${title}. Please make sure to RSVP through this message.`;

    this.state = {
      message,
      hasChildren: this.props.hasChildren || false,
      disableReplies: this.props.disableReplies || false,
      groupCodes: this.props.groupCodes || [],
      inComposer: false,
    };
  }

  getTotalMembershipCount() {
    if (this.props.showIndividualRecipients) {
      const { targets } = this.props;
      return targets.reduce((total, target) => {
        let count = 0;
        if (target.group_id) {
          // $FlowFixMe https://github.com/remind101/r101-frontend/pull/4065
          count = GroupStore.getGroup(target.group_id).getMembershipsCount();
        }
        return total + count;
      }, 0);
    } else {
      const { groupCodes } = this.state;
      return groupCodes.reduce((total, code) => {
        // $FlowFixMe https://github.com/remind101/r101-frontend/pull/4065
        const memberships = GroupStore.getGroupForCode(code).getMembershipsCount();
        return total + memberships;
      }, 0);
    }
  }

  getPublicState() {
    const publicState = {
      message: this.state.message,
      hasChildren: this.state.hasChildren,
      disableReplies: this.state.disableReplies,
      targets: this.props.targets,
    };

    if (!this.props.showIndividualRecipients) {
      const groupCodes = this.props.withRecipientInput ? this.state.groupCodes : [];
      const targets = getTargetsFromGroupCodes(groupCodes);

      publicState.targets.push(targets);
      publicState.targets = uniqBy(targets, t => t.group_id);
    }

    return publicState;
  }

  onNext: Function = (): void => {
    this.props.onNext(this.getPublicState());
  };

  handlePreviewClick: Function = (): void => {
    this.props.onPreviewClick(this.getPublicState());
  };

  onBack: Function = (): void => {
    this.props.onBack(this.getPublicState());
  };

  isValid: Function = (): boolean => {
    const selectedTargets = this.props.showIndividualRecipients ?
      this.props.selection :
      this.state.groupCodes;

    return (
      !!this.state.message && !(
        this.props.withRecipientInput &&
        selectedTargets.length === 0 &&
        this.props.targets.length === 0
      )
    );
  };

  handleInputFocus = (): void => {
    if (this.state.inComposer) {
      this.setState({ inComposer: false }, () => {
        if (this.props.onInComposerChange) {
          this.props.onInComposerChange(this.state.inComposer);
        }
      });
    }
  }

  handleRecipientsChange: Function = (groupCodes: string[]): void => {
    this.setState({ groupCodes });
  }

  getRepliesCheckboxLabel() {
    let label = 'Allow participants to reply to the group conversation';
    if (this.getTotalMembershipCount() > MAX_PARTICIPANTS_REPLIES_ENABLE) {
      label = `Replies are automatically turned off for groups with more than ${MAX_PARTICIPANTS_REPLIES_ENABLE} members`;
    }
    return label;
  }

  goToComposer = (): void => {
    if (!this.state.inComposer) {
      this.setState({ inComposer: true }, () => {
        if (this.props.onInComposerChange) {
          this.props.onInComposerChange(this.state.inComposer);
        }
      });
    }
  }

  renderRecipientsInput() {
    let input;

    if (this.props.showIndividualRecipients) {
      const { groupCodes } = this.props;

      // TODO: FIX: The initial message passed in is just a string, so message
      // targets who are individuals can't be detected and passed along to the
      // composer input.
      let initialTargets = [];
      if (groupCodes) {
        initialTargets = getTargetsFromGroupCodes(groupCodes);
      }
      const { inComposer } = this.state;

      input = (
        <RoleBasedMessagingRecipientsInput
          autoFocus={!inComposer}
          onInputFocus={this.handleInputFocus}
          initialTargets={initialTargets}
          onEnter={this.goToComposer}
          onActionButtonClick={this.goToComposer}
          hideResults={inComposer}
          showIndividuals={this.props.showIndividualRecipients}
        />
      );
    } else {
      input = (
        <AnnouncementComposerRecipientsInput
          selectedGroupCodes={this.state.groupCodes}
          onChange={this.handleRecipientsChange}
        />
      );
    }

    return (
      <div className="ActivityAnnouncementChatMessageFormView-recipientInput">
        {input}
      </div>
    );
  }

  render() {
    return (
      <span>
        {this.props.withRecipientInput && this.renderRecipientsInput()}
        {(this.state.inComposer || !this.props.showIndividualRecipients) &&
          <div className="ActivityAnnouncementChatMessageFormView-composer">
            <Textarea
              rows={4}
              data-test="activity-announcement-chat-message-form-view-body"
              className="ActivityAnnouncementChatMessageFormView-textarea"
              value={this.state.message}
              onChange={(event) => this.setState({ message: event.target.value })}
              placeholder="Add a note and any details. (An RSVP link will be included in your message.)"
            />

            <Checkbox
              defaultChecked={!this.state.hasChildren}
              label="The people responding to this activity are 13 years or older"
              onChange={(value) => this.setState({ hasChildren: !value.currentTarget.checked })}
            />

            <Checkbox
              disabled={this.state.hasChildren || this.getTotalMembershipCount() > MAX_PARTICIPANTS_REPLIES_ENABLE}
              defaultChecked={!this.state.disableReplies}
              label={this.getRepliesCheckboxLabel()}
              onChange={(value) => this.setState({ disableReplies: !value.currentTarget.checked })}
            />
            <PageControls
              className="ActivityAnnouncementChatMessageFormView-controls"
              disabledNext={!this.isValid()}
              onBack={this.onBack}
              onNext={this.onNext}
              onPreview={this.handlePreviewClick}
              nextText="Submit"
            />
          </div>
        }
      </span>
    );
  }
}

const mapStateToProps = (state: Object) => {
  return {
    selection: state.roleBasedMessagingSearch.selection,
    targets: state.roleBasedMessagingSearch.targets,
  };
};

export default connect(mapStateToProps)(ActivityAnnouncementChatMessageFormView);



// WEBPACK FOOTER //
// ./src/payments/components/ActivityAnnouncementChatMessageFormView.jsx